<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { subDays, getUnixTime, format } from 'date-fns';
import ReportsAPI from 'dashboard/api/reports';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ReportHeader from './components/ReportHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import Button from 'next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const { t } = useI18n();

// Lightweight rolling-window period picker - this report is an aggregate
// snapshot per ad, not a day-to-day trend view.
const PERIOD_DAYS = [7, 30, 90];
const PERIOD_LABEL_KEYS = {
  7: 'ADS_REPORTS.PERIOD.7',
  30: 'ADS_REPORTS.PERIOD.30',
  90: 'ADS_REPORTS.PERIOD.90',
};
const periodTabs = computed(() =>
  PERIOD_DAYS.map(days => ({ label: t(PERIOD_LABEL_KEYS[days]) }))
);
const activePeriodIndex = ref(1); // default 30 days
const periodDays = computed(() => PERIOD_DAYS[activePeriodIndex.value]);
const onPeriodTabChanged = tab => {
  activePeriodIndex.value = periodTabs.value.findIndex(
    candidate => candidate.label === tab.label
  );
};

const isFetching = ref(false);
// Bumped on every fetch; a response only commits if it's still the latest
// request in flight, so a stale response can't overwrite a newer one.
let requestId = 0;
const report = ref(null);

const fetchData = async () => {
  requestId += 1;
  const thisRequestId = requestId;
  isFetching.value = true;
  try {
    const now = new Date();
    const { data } = await ReportsAPI.getAdsOverview({
      from: getUnixTime(subDays(now, periodDays.value)),
      to: getUnixTime(now),
    });
    if (thisRequestId !== requestId) return;
    report.value = data;
  } catch {
    if (thisRequestId !== requestId) return;
    useAlert(t('ADS_REPORTS.FETCH_ERROR'));
  } finally {
    if (thisRequestId === requestId) isFetching.value = false;
  }
};

watch(periodDays, fetchData);
onMounted(fetchData);

const summary = computed(() => report.value?.summary || {});
const ads = computed(() => report.value?.ads || []);
const hasNoData = computed(
  () => !isFetching.value && !summary.value.conversations_count
);

const asOfLabel = computed(() => format(new Date(), 'MMM d, yyyy'));

const formatCsat = value => (value || value === 0 ? value.toFixed(2) : '—');
const resolvedPercent = row =>
  row.conversations_count
    ? `(${Math.round((row.resolved_count / row.conversations_count) * 100)}%)`
    : '';

// --- Campaign link maker: m.me/<page_id>?ref=<campaign> links are the only
// attribution mechanism for ads that link out to Messenger, so generate them
// here where the resulting campaigns are tracked.
const inboxes = useMapGetter('inboxes/getInboxes');
const facebookInboxes = computed(() =>
  inboxes.value.filter(
    inbox => inbox.channel_type === 'Channel::FacebookPage' && inbox.page_id
  )
);
const inboxOptions = computed(() =>
  facebookInboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const linkDialogRef = ref(null);
const selectedInboxId = ref(null);
const campaignName = ref('');
const campaignSource = ref('');
const campaignVariant = ref('');

const openLinkDialog = () => {
  selectedInboxId.value = facebookInboxes.value[0]?.id ?? null;
  campaignName.value = '';
  campaignSource.value = '';
  campaignVariant.value = '';
  linkDialogRef.value?.open();
};

// Parts exclude underscores so the `__` joiner stays unambiguous in the ref.
const slugify = value =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

const campaignRef = computed(() => {
  const name = slugify(campaignName.value);
  if (!name) return '';
  return [name, slugify(campaignSource.value), slugify(campaignVariant.value)]
    .filter(Boolean)
    .join('__');
});
const campaignLink = computed(() => {
  const inbox = facebookInboxes.value.find(
    candidate => candidate.id === selectedInboxId.value
  );
  if (!inbox || !campaignRef.value) return '';
  return `https://m.me/${inbox.page_id}?ref=${campaignRef.value}`;
});

const copyCampaignLink = async () => {
  if (!campaignLink.value) return;
  await copyTextToClipboard(campaignLink.value);
  useAlert(t('ADS_REPORTS.LINK_MAKER.COPIED'));
  linkDialogRef.value?.close();
};
</script>

<template>
  <ReportHeader :header-title="t('ADS_REPORTS.HEADER')" />
  <div class="flex flex-col gap-6 pb-8">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <TabBar
        :tabs="periodTabs"
        :initial-active-tab="activePeriodIndex"
        @tab-changed="onPeriodTabChanged"
      />
      <div class="flex items-center gap-3">
        <span class="text-sm text-n-slate-11">{{ asOfLabel }}</span>
        <Button
          v-if="facebookInboxes.length"
          :label="t('ADS_REPORTS.LINK_MAKER.BUTTON')"
          icon="i-lucide-link"
          sm
          @click="openLinkDialog"
        />
      </div>
    </div>

    <woot-loading-state v-if="isFetching" :message="t('ADS_REPORTS.LOADING')" />
    <p v-else-if="hasNoData" class="text-sm text-n-slate-11">
      {{ t('ADS_REPORTS.EMPTY') }}
    </p>

    <template v-else>
      <section class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <ReportMetricCard
          :label="t('ADS_REPORTS.SUMMARY.CONVERSATIONS')"
          :value="String(summary.conversations_count ?? 0)"
          :info-text="t('ADS_REPORTS.SUMMARY.CONVERSATIONS_INFO')"
        />
        <ReportMetricCard
          :label="t('ADS_REPORTS.SUMMARY.RESOLVED')"
          :value="String(summary.resolved_count ?? 0)"
          :info-text="t('ADS_REPORTS.SUMMARY.RESOLVED_INFO')"
        />
        <ReportMetricCard
          :label="t('ADS_REPORTS.SUMMARY.CSAT')"
          :value="formatCsat(summary.csat_rating)"
          :info-text="t('ADS_REPORTS.SUMMARY.CSAT_INFO')"
        />
      </section>

      <div class="overflow-x-auto border rounded-xl border-n-weak">
        <table class="w-full text-sm">
          <thead class="bg-n-solid-2">
            <tr>
              <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
                {{ t('ADS_REPORTS.TABLE.AD') }}
              </th>
              <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
                {{ t('ADS_REPORTS.TABLE.PAGE') }}
              </th>
              <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
                {{ t('ADS_REPORTS.TABLE.REF') }}
              </th>
              <th class="px-4 py-2.5 text-right font-medium text-n-slate-11">
                {{ t('ADS_REPORTS.TABLE.CONVERSATIONS') }}
              </th>
              <th class="px-4 py-2.5 text-right font-medium text-n-slate-11">
                {{ t('ADS_REPORTS.TABLE.RESOLVED') }}
              </th>
              <th class="px-4 py-2.5 text-right font-medium text-n-slate-11">
                {{ t('ADS_REPORTS.TABLE.CSAT') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="row in ads" :key="`${row.inbox_id}-${row.ad_key}`">
              <td class="px-4 py-2.5">
                <div class="flex flex-col">
                  <span class="text-n-slate-12">
                    {{ row.ad_title || row.ref || row.ad_id }}
                  </span>
                  <span
                    v-if="row.ad_id"
                    class="text-xs tabular-nums text-n-slate-10"
                  >
                    {{ row.ad_id }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-2.5 text-n-slate-11">
                {{ row.inbox_name || '—' }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-11">
                {{ row.ref || '—' }}
              </td>
              <td class="px-4 py-2.5 text-right tabular-nums text-n-slate-12">
                {{ row.conversations_count }}
              </td>
              <td class="px-4 py-2.5 text-right tabular-nums text-n-slate-12">
                {{ row.resolved_count }}
                <span class="text-xs text-n-slate-10">
                  {{ resolvedPercent(row) }}
                </span>
              </td>
              <td class="px-4 py-2.5 text-right tabular-nums text-n-slate-12">
                {{ formatCsat(row.csat_rating) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <Dialog
      ref="linkDialogRef"
      type="edit"
      :title="t('ADS_REPORTS.LINK_MAKER.TITLE')"
      :description="t('ADS_REPORTS.LINK_MAKER.DESCRIPTION')"
      :confirm-button-label="t('ADS_REPORTS.LINK_MAKER.COPY')"
      :disable-confirm-button="!campaignLink"
      @confirm="copyCampaignLink"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ADS_REPORTS.LINK_MAKER.INBOX_LABEL') }}
          </span>
          <ComboBox v-model="selectedInboxId" :options="inboxOptions" />
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ADS_REPORTS.LINK_MAKER.NAME_LABEL') }}
          </span>
          <Input
            v-model="campaignName"
            :placeholder="t('ADS_REPORTS.LINK_MAKER.NAME_PLACEHOLDER')"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('ADS_REPORTS.LINK_MAKER.SOURCE_LABEL') }}
            </span>
            <Input
              v-model="campaignSource"
              :placeholder="t('ADS_REPORTS.LINK_MAKER.SOURCE_PLACEHOLDER')"
            />
          </div>
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('ADS_REPORTS.LINK_MAKER.VARIANT_LABEL') }}
            </span>
            <Input
              v-model="campaignVariant"
              :placeholder="t('ADS_REPORTS.LINK_MAKER.VARIANT_PLACEHOLDER')"
            />
          </div>
        </div>
        <div v-if="campaignLink" class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ADS_REPORTS.LINK_MAKER.LINK_LABEL') }}
          </span>
          <code
            class="px-3 py-2 text-sm break-all rounded-lg bg-n-alpha-black2 text-n-slate-12"
          >
            {{ campaignLink }}
          </code>
        </div>
      </div>
    </Dialog>
  </div>
</template>
